import clsx from "clsx"
import React, { JSX } from "react"
import { BaseComponentWithChildrenProps } from "../../utils"

/**
 * 타이포그래피 컴포넌트
 *
 * @description
 * 모든 텍스트 표시에 사용하는 다형성 컴포넌트입니다.
 * variant로 크기/굵기, color로 색상을 제어합니다. 기본 HTML 태그는 `<p>`.
 *
 * @example
 * ```tsx
 * <TText variant="title">섹션 제목</TText>
 * <TText variant="caption" color="secondary">부가 설명</TText>
 * <TText variant="content" bold>강조 텍스트</TText>
 * <TText component="h1" variant="page">페이지 제목</TText>
 * <TText truncate>{longText}</TText>
 * <TText align="center" variant="caption" color="secondary">중앙 정렬</TText>
 * ```
 */
interface TTextProps extends Omit<BaseComponentWithChildrenProps, "dataAttributes"> {
	/**
	 * 렌더링할 HTML 태그
	 * @default "p"
	 * @example component="h1", component="span", component="label"
	 */
	component?: keyof JSX.IntrinsicElements
	/** `component="label"`일 때 연결할 폼 요소의 id (네이티브 `<label htmlFor>`) */
	htmlFor?: string
	/**
	 * 텍스트 크기/굵기 프리셋
	 * @default "content"
	 * @ai-style "page" → text-4xl font-bold — 페이지 최상위 제목
	 * @ai-style "display" → text-3xl font-bold — 대형 표시
	 * @ai-style "headline" → text-2xl font-bold — 헤드라인
	 * @ai-style "title" → text-xl font-bold — 섹션 제목
	 * @ai-style "subtitle" → text-lg font-bold — 소제목
	 * @ai-style "content" → text-base — 본문 텍스트 (기본)
	 * @ai-style "caption" → text-sm — 캡션/부가 정보
	 * @ai-style "help" → text-xs — 도움말/주석
	 * @ai-style "tiny" → 10px font-bold — 아주 작은 라벨
	 */
	variant?: "page" | "display" | "headline" | "title" | "subtitle" | "content" | "caption" | "help" | "tiny"
	/**
	 * 텍스트 색상
	 * @default "text"
	 * @ai-style "text" → 기본 텍스트 색상
	 * @ai-style "primary" → 녹색 강조 (Primary 브랜드 색상)
	 * @ai-style "secondary" → 보조 텍스트 (덜 중요한 정보)
	 * @ai-style "tertiary" → 3차 텍스트 (가장 연한)
	 * @ai-style "placeholder" → 플레이스홀더 색상
	 * @ai-style "disabled" → 비활성 텍스트
	 * @ai-style "error" → 에러 빨간색
	 * @ai-style "warning" → 경고 노란색(#ffc300)
	 * @ai-style "black" → 순수 검정
	 */
	color?: "text" | "primary" | "warning" | "error" | "secondary" | "tertiary" | "placeholder" | "disabled" | "black"
	/** 굵게 표시 (font-bold 추가) */
	bold?: boolean
	/** 얇게 표시 (font-normal 강제 적용, bold variant에서 굵기 해제 시) */
	light?: boolean
	/** 텍스트 말줄임 (overflow: hidden + text-overflow: ellipsis + white-space: nowrap) */
	truncate?: boolean
	/**
	 * 텍스트 정렬
	 * @ai-style "left" → text-align: left
	 * @ai-style "center" → text-align: center
	 * @ai-style "right" → text-align: right
	 */
	align?: "left" | "center" | "right"
	/**
	 * 줄바꿈 방식
	 * @ai-style "nowrap" → white-space: nowrap (줄바꿈 없음)
	 * @ai-style "normal" → white-space: normal (기본 줄바꿈)
	 * @ai-style "break-all" → word-break: break-all (단어 중간에서도 줄바꿈)
	 */
	wrap?: "nowrap" | "normal" | "break-all"
	/** 번역 제외 (translate="no" 속성 추가) */
	noTranslate?: boolean
}

export const TText = React.memo((props: TTextProps) => {
	const { component: Component = "p", variant = "content", color = "text", bold, light, truncate, align, wrap, noTranslate, className, children, ...restProps } = props

	const alignClass = align === "center" ? "text-center" : align === "right" ? "text-right" : align === "left" ? "text-left" : undefined
	const wrapClass = truncate ? undefined : wrap === "nowrap" ? "whitespace-nowrap" : wrap === "break-all" ? "break-all" : wrap === "normal" ? "whitespace-normal" : undefined

	return (
		<Component
			{...(restProps as Record<string, unknown>)}
			className={clsx("TText", variant, color, { "font-bold": bold, "font-normal!": light, truncate, noTranslate }, alignClass, wrapClass, className)}
			{...(noTranslate && { translate: "no" })}
		>
			{children}
		</Component>
	)
})

TText.displayName = "TText"
